// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Workspace users need to understand what this knowledge base feeds
 * before adding or changing items. Show the administrator-authored label,
 * description, and mode in the header; a generic title hides that relationship
 * and can lead users to curate content for the wrong analysis behavior. Keep
 * the mode chip and explanation aligned with the settings administrators edit.
 */
import { type FC } from 'react'

import { Alert, AlertDescription } from '@/components/ui'

import type { NodeUserViewProps } from '../../../../core/types'
import { KnowledgeBaseProvider, useKnowledgeBaseStore } from '../core/KnowledgeBaseProvider'
import { useKnowledgeBaseScreen } from '../hooks/useKnowledgeBaseScreen'
import { resolveSettingValue } from '../utils/settings'
import { KbHowItWorks, MODE_CHIP } from '../ui/KbHowItWorks'
import { StatsHeader } from '../ui/StatsHeader'
import { KnowledgeBaseTabs } from './KnowledgeBaseTabs'

interface Props extends NodeUserViewProps {
  wsId?: string
  showHeader?: boolean
}

export const KnowledgeBaseUserView: FC<Props> = props => {
  return (
    <KnowledgeBaseProvider {...props}>
      <KnowledgeBaseScreen workspaceLabel={props.wsId ?? props.workspaceId ?? 'default-workspace'} config={props.config} showHeader={props.showHeader} />
    </KnowledgeBaseProvider>
  )
}

interface KnowledgeBaseScreenProps {
  workspaceLabel: string
  config: unknown
  showHeader?: boolean
}

export function KnowledgeBaseScreen({ workspaceLabel, config, showHeader = true }: KnowledgeBaseScreenProps): JSX.Element {
  const {
    store,
    settings,
    schema,
    browser,
    readOnly,
    disableActions,
    handleError,
    handleCreateItem,
    handleUpdateItem,
    handleDeleteItem,
  } = useKnowledgeBaseScreen({ config, workspaceLabel })
  const kbStore = useKnowledgeBaseStore()

  // Don't render until settings are loaded from API
  if (store.loading) {
    return (
      <div className="rounded-xl border border-[#e7ecf2] bg-white px-6 py-8 text-[13px] text-slate-500 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
        Loading knowledge base…
      </div>
    )
  }

  const label = resolveSettingValue(kbStore.settings, 'label').trim()
  const description = resolveSettingValue(kbStore.settings, 'description').trim()

  const content = (
    <div className="space-y-4 sm:space-y-5">
        {store.error && (
          <Alert variant="destructive">
            <AlertDescription>{store.error}</AlertDescription>
          </Alert>
        )}

        <KbHowItWorks mode={settings.mode} />

        <StatsHeader
          localCount={browser.counts.local}
          sharedCount={browser.counts.shared}
          onRefresh={() => { void store.reload() }}
          disabled={disableActions}
          isRefreshing={store.loading}
          timestamp={store.data.timestamp}
        />

        <KnowledgeBaseTabs
          browser={browser}
          readOnly={readOnly}
          disableActions={disableActions}
          items={store.data.items}
          settings={settings}
          schema={schema}
          saving={store.saving}
          onCreateItem={handleCreateItem}
          onUpdateItem={handleUpdateItem}
          onDeleteItem={handleDeleteItem}
          onError={handleError}
        />
    </div>
  )

  if (!showHeader) return content

  return (
    <div className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <div className="border-b border-[#eef2f6] px-4 py-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="text-sm font-semibold tracking-[-0.01em]">{label || 'Knowledge Base'}</h2>
          <span className="rounded-full bg-[#eff6ff] px-2.5 py-[3px] text-[11px] font-semibold text-[#2563eb]">
            {MODE_CHIP[settings.mode]}
          </span>
          <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-[3px] font-mono text-[11.5px] text-slate-600">
            {workspaceLabel}
          </span>
        </div>
        <p className="mt-[3px] text-[12.5px] text-slate-500">
          {description || 'Guidance the AI receives every time it analyzes documents in this workspace.'}
        </p>
      </div>
      <div className="px-4 py-4 sm:px-6">
        {content}
      </div>
    </div>
  )
}
